import { Box, Field, Input, Stack, Text, VStack, Link } from "@chakra-ui/react"
import { useState, useCallback, useEffect } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { authClient, acceptInvitation } from "./auth"
import { UpdateButton } from "@hydrologiq/shared/content/components/Button"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"

export function SignInForm() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // Get parameters from URL
  const urlEmail = searchParams.get("email") || ""
  const urlMessage = searchParams.get("message") || ""
  const invitationId = searchParams.get("invitation") || ""

  // Pre-fill email from URL
  useEffect(() => {
    if (urlEmail && !email) {
      setEmail(urlEmail)
    }
  }, [urlEmail, email])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      setLoading(true)

      try {
        const result = await authClient.signIn.email({
          email,
          password,
        })

        if (result.error) {
          setError(result.error.message || "Failed to sign in")
          return
        }

        // If there's an invitation ID, try to accept it after successful sign-in
        if (invitationId) {
          try {
            const invitationResult = await acceptInvitation({ invitationId })

            if (invitationResult?.data) {
              toaster.success({
                title: "Invitation accepted",
                description: "You've successfully joined the organization",
              })
            }
          } catch (invitationError) {
            console.warn("Failed to auto-accept invitation:", invitationError)
            // Don't fail the sign-in for this, just log it
            toaster.warning({
              title: "Sign in successful",
              description: "Please manually accept your invitation from the invitation link",
            })
          }
        }

        // Redirect to home or reload
        if (invitationId) {
          navigate("/")
        } else {
          window.location.reload()
        }
      } catch (err) {
        setError("An unexpected error occurred")
      } finally {
        setLoading(false)
      }
    },
    [email, password, invitationId, navigate]
  )

  return (
    <Box as="form" onSubmit={handleSubmit} w="full" maxW="md" p={8} borderRadius="md" boxShadow="md">
      <VStack gap={6} align="stretch">
        <Field.Root required>
          <Field.Label>Email</Field.Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            disabled={loading}
          />
        </Field.Root>

        <Field.Root required>
          <Field.Label>Password</Field.Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            disabled={loading}
          />
        </Field.Root>

        <Stack direction="row" justify="flex-end" w="full" fontSize="sm">
          <Link href="/auth/forgot-password" variant="primary">
            Forgot password?
          </Link>
        </Stack>

        {urlMessage && (
          <Text color="blue.600" fontSize="sm" textAlign="center">
            {urlMessage}
          </Text>
        )}

        {error && (
          <Text color="error" fontSize="sm">
            {error}
          </Text>
        )}

        <UpdateButton
          context={{ label: loading ? "Signing in..." : "Sign In", description: "Sign in to your account" }}
          onClick={handleSubmit}
          type="submit"
        />

        <Stack direction="row" justify="center" gap={1} fontSize="sm">
          <Text>Need an account? Request one through your organisation.</Text>
        </Stack>
      </VStack>
    </Box>
  )
}
